import React, { useEffect } from 'react';
import { Container, Grid, Paper, Typography, Button, Box, Alert, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { 
  Add as AddIcon, 
  List as ListIcon,
  Assignment as AssignmentIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  Message as MessageIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import DashboardCharts from '../components/DashboardCharts';
import NotificationSettingsPanel from '../components/NotificationSettingsPanel';

// Функция за извличане на статистика
const getUserStats = async () => {
  try {
    const response = await api.get('/api/v1/me/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return { 
      total_requests: 0, 
      completed_requests: 0, 
      pending_requests: 0,
      active_requests: 0,
      unread_messages: 0
    };
  }
};

// Функция за извличане на данни за графики
const getUserChartData = async () => {
  try {
    const response = await api.get('/api/v1/me/chart-data');
    return response.data;
  } catch (error) {
    console.error('Error fetching chart data:', error);
    return null;
  }
};

interface UserStats {
  total_requests: number;
  completed_requests: number;
  pending_requests: number;
  active_requests: number;
  unread_messages: number;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useWebSocket();
  const queryClient = useQueryClient();
  const actionCardSx = {
    p: 3,
    height: '100%',
    borderRadius: 2,
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    },
  };

  const displayName = (() => {
    const formatNameCandidate = (value?: string) => {
      const cleaned = value?.trim();
      if (!cleaned) return '';

      // Ако стойността е email, ползваме частта преди @.
      const localPart = cleaned.includes('@') ? cleaned.split('@')[0] : cleaned;
      return localPart.replace(/[._-]+/g, ' ').trim();
    };

    const genericRoleLabels = new Set([
      'professional',
      'client',
      'професионалист',
      'клиент',
    ]);

    const fullName = formatNameCandidate(user?.full_name);
    if (fullName && !genericRoleLabels.has(fullName.toLowerCase())) {
      return fullName;
    }

    const username = formatNameCandidate(user?.username);
    if (username) {
      return username;
    }

    const emailName = formatNameCandidate(user?.email);
    if (emailName) {
      return emailName;
    }

    return t('common.user');
  })();
  
  // Логваме user типа при всяко рендериране и изчистваме кеша, ако типа се промени
  useEffect(() => {
    if (user) {
      // Определяме типа потребител от няколко източника
      const currentUserType = user.user_type || localStorage.getItem('user_type') || 
                            (user.email === 'mitat1976@abv.bg' ? 'professional' : 'client');
      const userId = user.id?.toString() || localStorage.getItem('user_id') || '1';
      
      // Запазваме типа потребител в localStorage
      localStorage.setItem('user_type', currentUserType);
      localStorage.setItem('user_id', userId);
      
      const currentPath = window.location.pathname;
      
      // Проверяваме дали URL съответства на типа потребител
      const isProfessionalPath = currentPath.includes('/profile/professional/');
      const isClientPath = currentPath.includes('/profile/client/');
      
      // Ако URL не съответства на типа потребител, пренасочваме
      if ((currentUserType === 'professional' && !isProfessionalPath) || 
          (currentUserType === 'client' && !isClientPath)) {
        navigate(`/profile/${currentUserType}/${userId}/dashboard`);
        return;
      }
      
      // Проверяваме дали потребителят е на правилната страница
      const isOnCorrectPath = 
        (currentUserType === 'professional' && currentPath.includes(`/profile/professional/${userId}/dashboard`)) ||
        (currentUserType === 'client' && currentPath.includes(`/profile/client/${userId}/dashboard`));
      
      if (!isOnCorrectPath) {
        navigate(`/profile/${currentUserType}/${userId}/dashboard`);
      }
    }
  }, [user, navigate]);
  
  const { data: stats, isLoading: statsLoading } = useQuery<UserStats>({
    queryKey: ['userStats', user?.id],
    queryFn: getUserStats,
    refetchInterval: 5000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });

  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['userChartData', user?.id],
    queryFn: getUserChartData,
    refetchInterval: 30000, // Обновяваме на всеки 30 секунди
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });

  // WebSocket listener за нови съобщения - обновява статистиката
  useEffect(() => {
    if (!socket || !user) return;

    const handleWebSocketMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        // При ново съобщение или промяна в статус на задача/оферта
        if (data.type === 'new_message' || 
            data.type === 'chat_message' || 
            data.type === 'task_updated' || 
            data.type === 'bid_updated') {
          // Инвалидираме кеша на статистиката, за да се презареди
          queryClient.invalidateQueries({ queryKey: ['userStats', user.id] });
        }
      } catch (error) {
        console.error('Dashboard: Error parsing WebSocket message:', error);
      }
    };

    socket.addEventListener('message', handleWebSocketMessage);

    return () => {
      socket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [socket, user, queryClient]);

  const StatisticsSection = () => (
    <Grid item xs={12}>
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('dashboard.statistics')}
        </Typography>
        
        {statsLoading ? (
          <Grid container spacing={3}>
            {[...Array(3)].map((_, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton width="50%" sx={{ mx: 'auto' }} />
                  <Skeleton width="60%" sx={{ mx: 'auto' }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : stats ? (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 2, color: 'primary.contrastText' }}>
                  <ListIcon sx={{ fontSize: 40 }} />
                  <Typography variant="h4">{stats.total_requests}</Typography>
                  <Typography variant="body2">{t('dashboard.total_requests')}</Typography>
                </Paper>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, color: 'success.contrastText' }}>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                  <Typography variant="h4">{stats.completed_requests}</Typography>
                  <Typography variant="body2">{t('dashboard.completed_requests')}</Typography>
                </Paper>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  p: 2,
                  '&:hover': { 
                    cursor: 'pointer',
                    opacity: 0.9, 
                  } 
                }}
                onClick={() => navigate('/messages')}
              >
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'secondary.main', 
                    borderRadius: 2, 
                    color: 'secondary.contrastText',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  <MessageIcon sx={{ fontSize: 40 }} />
                  <Typography variant="h4">{stats?.unread_messages || 0}</Typography>
                  <Typography variant="body2">{t('dashboard.unread_messages')}</Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Alert icon={<ErrorIcon />} severity="error">
            {t('errors.load_stats_failed')}
          </Alert>
        )}
      </Paper>
    </Grid>
  );

  const ClientDashboard = () => (
    <Grid container spacing={3}>
      {/* 1. Бутони за задачи - НАЙ-ОТГОРЕ */}
      <Grid item xs={12} md={6}>
        <Paper sx={actionCardSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', gap: 2 }}>
            <AddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {t('dashboard.create_task')}
            </Typography>
            <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              {t('dashboard.create_task_description')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tasks/create')}
              sx={{ mt: 'auto' }}
            >
              {t('dashboard.create_task_button')}
            </Button>
          </Box>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Paper sx={actionCardSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', gap: 2 }}>
            <ListIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {t('dashboard.my_tasks')}
            </Typography>
            <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              {t('dashboard.my_tasks_description')}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ListIcon />}
              onClick={() => navigate('/tasks/my')}
              sx={{ mt: 'auto' }}
            >
              {t('dashboard.view_tasks_button')}
            </Button>
          </Box>
        </Paper>
      </Grid>
      
      {/* 2. Статистики - по средата */}
      <StatisticsSection />
      
      {/* 3. Графики - най-отдолу */}
      <Grid item xs={12}>
        <DashboardCharts chartData={chartData} userType="client" loading={chartLoading} />
      </Grid>
    </Grid>
  );

  const ProfessionalDashboard = () => (
    <Grid container spacing={3}>
      {/* 1. Бутони за задачи и оферти - НАЙ-ОТГОРЕ */}
      <Grid item xs={12} md={6}>
        <Paper sx={actionCardSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', gap: 2 }}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {t('dashboard.available_tasks')}
            </Typography>
            <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              {t('dashboard.available_tasks_description')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/tasks')}
              sx={{ mt: 'auto' }}
            >
              {t('dashboard.browse_tasks_button')}
            </Button>
          </Box>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Paper sx={actionCardSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', gap: 2 }}>
            <GavelIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {t('dashboard.my_bids')}
            </Typography>
            <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              {t('dashboard.my_bids_description')}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<GavelIcon />}
              onClick={() => navigate('/bids/my')}
              sx={{ mt: 'auto' }}
            >
              {t('dashboard.view_bids_button')}
            </Button>
          </Box>
        </Paper>
      </Grid>
      
      {/* 2. Статистики - по средата */}
      <StatisticsSection />

      <Grid item xs={12}>
        <NotificationSettingsPanel variant="professional" />
      </Grid>
      
      {/* 3. Графики - най-отдолу */}
      <Grid item xs={12}>
        <DashboardCharts chartData={chartData} userType="professional" loading={chartLoading} />
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 'bold' }} data-testid="dashboard-title">
        {t('dashboard.welcome')}, {displayName}!
      </Typography>
      {(user?.user_type === 'professional' && window.location.pathname.includes('/profile/professional/dashboard')) || 
       (user?.user_type === 'client' && !window.location.pathname.includes('/profile/professional/dashboard')) 
        ? (user?.user_type === 'client' ? <ClientDashboard /> : <ProfessionalDashboard />)
        : (user?.user_type === 'professional' ? <ProfessionalDashboard /> : <ClientDashboard />)
      }
    </Container>
  );
};

export default Dashboard; 